import { useEffect, useRef, useState } from 'react';
import { CheckIcon, Loader2Icon, XIcon } from 'lucide-react';

import { Chip, type ChipTone } from '@/components/chip';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { CreateItem } from './combobox-create-item';

export interface ComboboxOptionBadge {
  label: string;
  tone?: ChipTone;
}

export interface ComboboxOption {
  value: string;
  label: string;
  description?: string;
  keywords?: string[];
  badge?: ComboboxOptionBadge;
  disabled?: boolean;
}

export interface ComboboxListProps {
  selected: ReadonlySet<string>;
  options: readonly ComboboxOption[];
  showSearch: boolean;
  isAsync: boolean;
  multi: boolean;
  clearable: boolean;
  searchPlaceholder: string;
  searchValue: string | undefined;
  onSearchChange: ((q: string) => void) | undefined;
  emptyMessage: string;
  loading: boolean | undefined;
  loadingMessage: string;
  onSelect: (value: string) => void;
  onClear: () => void;
  // Opt-in creatable action (single-select); undefined elsewhere.
  onCreate: ((label: string) => void) | undefined;
}

interface OptionsProps {
  selected: ReadonlySet<string>;
  options: readonly ComboboxOption[];
  multi: boolean;
  clearable: boolean;
  onSelect: (value: string) => void;
  onClear: () => void;
}

function ClearItem({ multi, onClear }: Readonly<{ multi: boolean; onClear: () => void }>) {
  return (
    <CommandItem
      value="__clear__"
      keywords={['clear', 'reset', 'none']}
      onSelect={onClear}
      className="text-muted-foreground"
    >
      <XIcon className="size-4" aria-hidden />
      {multi ? 'Clear all' : 'Clear selection'}
    </CommandItem>
  );
}

function OptionItem({
  option,
  selected,
  onSelect,
}: Readonly<{
  option: ComboboxOption;
  selected: ReadonlySet<string>;
  onSelect: (value: string) => void;
}>) {
  const keywords = [option.label, ...(option.keywords ?? [])];
  if (option.description !== undefined) keywords.push(option.description);
  if (option.badge !== undefined) keywords.push(option.badge.label);
  return (
    <CommandItem
      key={option.value}
      value={option.value}
      keywords={keywords}
      disabled={option.disabled === true}
      onSelect={() => {
        if (option.disabled === true) return;
        onSelect(option.value);
      }}
    >
      <CheckIcon
        className={cn('size-4 shrink-0', selected.has(option.value) ? 'opacity-100' : 'opacity-0')}
        aria-hidden
      />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="break-words whitespace-normal">{option.label}</span>
          {option.badge !== undefined && (
            <Chip tone={option.badge.tone ?? 'warning'} size="sm" className="shrink-0">
              {option.badge.label}
            </Chip>
          )}
        </div>
        {option.description !== undefined && (
          <span className="text-muted-foreground text-xs break-words whitespace-normal">
            {option.description}
          </span>
        )}
      </div>
    </CommandItem>
  );
}

function ComboboxOptions({
  selected,
  options,
  multi,
  clearable,
  onSelect,
  onClear,
}: Readonly<OptionsProps>) {
  const showClear = clearable && selected.size > 0;
  return (
    <CommandGroup>
      {showClear && <ClearItem multi={multi} onClear={onClear} />}
      {options.map((option) => (
        <OptionItem key={option.value} option={option} selected={selected} onSelect={onSelect} />
      ))}
    </CommandGroup>
  );
}

function LoadingRow({ loadingMessage }: Readonly<{ loadingMessage: string }>) {
  return (
    <div
      role="status"
      className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm"
    >
      <Loader2Icon className="size-4 animate-spin" aria-hidden />
      {loadingMessage}
    </div>
  );
}

// Radix Dialog's scroll lock (react-remove-scroll) blocks wheel events on the
// portaled combobox popover, so the trackpad can't scroll the option list even
// though the scrollbar is draggable. Scroll the list manually within its bounds
// and let the edges bubble so we never double-scroll outside a dialog.
function useDialogWheelScroll() {
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return undefined;
    const onWheel = (event: WheelEvent) => {
      const list = container.querySelector<HTMLElement>('[data-slot="command-list"]');
      if (list === null) return;
      const { scrollTop, scrollHeight, clientHeight } = list;
      const scrollingUp = event.deltaY < 0;
      const atTop = scrollTop <= 0;
      const atBottom = scrollTop + clientHeight >= scrollHeight;
      if ((scrollingUp && atTop) || (!scrollingUp && atBottom)) return;
      event.preventDefault();
      list.scrollTop = scrollTop + wheelDeltaPx(event, clientHeight);
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      container.removeEventListener('wheel', onWheel);
    };
  }, []);
  return containerRef;
}

// deltaY is reported in lines (mode 1) or pages (mode 2) for some mice; pixels (0) otherwise.
function wheelDeltaPx(event: WheelEvent, pageHeight: number): number {
  if (event.deltaMode === 1) return event.deltaY * 16;
  if (event.deltaMode === 2) return event.deltaY * pageHeight;
  return event.deltaY;
}

interface ComboboxBodyProps {
  selected: ReadonlySet<string>;
  options: readonly ComboboxOption[];
  multi: boolean;
  clearable: boolean;
  onSelect: (value: string) => void;
  onClear: () => void;
  loading: boolean | undefined;
  loadingMessage: string;
  emptyMessage: string;
  creatable: boolean;
  createQuery: string;
  onCreate: ((label: string) => void) | undefined;
}

function ComboboxBody(props: Readonly<ComboboxBodyProps>) {
  if (props.loading === true) return <LoadingRow loadingMessage={props.loadingMessage} />;
  // The "Add" row answers an empty result, so it replaces the empty message
  // rather than stacking under it. With nothing typed there is no such row,
  // so a creatable picker with no options still says why it is empty.
  const showEmpty = !props.creatable || props.createQuery.trim() === '';
  return (
    <>
      {showEmpty && <CommandEmpty>{props.emptyMessage}</CommandEmpty>}
      <ComboboxOptions
        selected={props.selected}
        options={props.options}
        multi={props.multi}
        clearable={props.clearable}
        onSelect={props.onSelect}
        onClear={props.onClear}
      />
      {props.creatable && props.onCreate !== undefined && (
        <CreateItem query={props.createQuery} options={props.options} onCreate={props.onCreate} />
      )}
    </>
  );
}

export function ComboboxList(props: Readonly<ComboboxListProps>) {
  const { showSearch, isAsync, searchPlaceholder, searchValue, onSearchChange, onCreate } = props;
  const wheelRef = useDialogWheelScroll();
  // A client-filtered picker keeps the query locally; an async picker already
  // holds it in searchValue, and the create row reads the same text so a
  // server-searched picker can still offer "Add".
  const [localQuery, setLocalQuery] = useState('');
  const creatable = onCreate !== undefined;
  const localInput = creatable && !isAsync;
  const inputValue = localInput ? localQuery : searchValue;
  const inputOnChange = localInput ? setLocalQuery : onSearchChange;
  const createQuery = (isAsync ? searchValue : localQuery) ?? '';
  return (
    <div ref={wheelRef} className="contents">
      <Command shouldFilter={!isAsync} className="max-h-[inherit]">
        <div className={showSearch ? '' : 'sr-only'}>
          <CommandInput
            placeholder={searchPlaceholder}
            {...(inputValue === undefined ? {} : { value: inputValue })}
            {...(inputOnChange === undefined ? {} : { onValueChange: inputOnChange })}
          />
        </div>
        <CommandList>
          <ComboboxBody
            selected={props.selected}
            options={props.options}
            multi={props.multi}
            clearable={props.clearable}
            onSelect={props.onSelect}
            onClear={props.onClear}
            loading={props.loading}
            loadingMessage={props.loadingMessage}
            emptyMessage={props.emptyMessage}
            creatable={creatable}
            createQuery={createQuery}
            onCreate={onCreate}
          />
        </CommandList>
      </Command>
    </div>
  );
}
